import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Paper } from '@mui/material';
import Grid from '@mui/material/Grid2';
import Loader from 'app/common/Loader';
import TraceabilityTimeline, { Field } from 'app/main/components/circularity/TraceabilityTimeline';
import { formatUnits, formatQty } from 'app/common/numberFormat';
import { getPublicCircularityTraceability } from 'app/services/api/public/traceability';
import PageHeader from '../components/PageHeader/PageHeader';

/** Fecha dd/mm/aaaa a partir del ISO que entrega el backend. */
const fmtDate = value => {
	if (!value) return '—';
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return '—';
	return d.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/**
 * Consulta PÚBLICA de la trazabilidad de un certificado de circularidad.
 *
 * Es el destino de la URL única impresa (con QR) en el PDF del certificado:
 * `/trazabilidad/:serial`. No requiere autenticación y muestra exactamente la
 * misma cadena que la plataforma, reutilizando `TraceabilityTimeline`.
 */
const PublicTraceability = () => {
	const { serial } = useParams();
	const [loading, setLoading] = useState(true);
	const [certificate, setCertificate] = useState(null);
	const [trace, setTrace] = useState(null);
	const [notFound, setNotFound] = useState(false);

	useEffect(() => {
		(async () => {
			setLoading(true);
			const { be, data } = (await getPublicCircularityTraceability(serial)) || {};
			setLoading(false);
			if (be !== 'ok' || !data?.found) {
				setNotFound(true);
				return;
			}
			setCertificate(data.certificate || null);
			setTrace(data.traceability || null);
		})();
	}, [serial]);

	return (
		<>
			<Loader visible={loading} />

			<PageHeader
				eyebrow="Certificado de circularidad"
				title="Trazabilidad del material"
				subtitle={`Consulta pública de la cadena de circularidad del certificado ${serial}`}
			/>

			<div className="bg-[#F8FAFC] min-h-[calc(100vh-280px)]">
				<div className="max-w-[1100px] mx-auto py-64 px-40 max-sm:px-16 max-sm:py-32">
					{!loading && notFound && (
						<Alert severity="warning">
							No se encontró un certificado de circularidad verificado con el número{' '}
							<strong>{serial}</strong>. Verifica el número impreso en el certificado.
						</Alert>
					)}

					{certificate && (
						<Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: '16px', bgcolor: '#fff' }}>
							<Grid container spacing={2}>
								<Field label="Número de certificado" value={certificate.serial} span={12} />
								<Field label="Titular del certificado" value={certificate.holderName} span={6} />
								<Field label="Número de identificación" value={certificate.holderNit} span={6} />
								<Field label="Tipo de material" value={certificate.material} span={3} />
								<Field label="Cantidad (Ton)" value={formatQty(certificate.materialTons)} span={3} />
								<Field
									label="Producto final"
									value={`${formatUnits(certificate.unitsSold)} ${certificate.product}`}
									span={3}
								/>
								<Field
									label="Fecha de verificación"
									value={fmtDate(certificate.verificationDate)}
									span={3}
								/>
								<Field label="Fabricante" value={certificate.manufacturerName} span={6} />
								<Field label="Periodo de venta" value={certificate.period} span={6} />
							</Grid>
						</Paper>
					)}

					{trace && (
						<Paper variant="outlined" sx={{ p: 2.5, borderRadius: '16px', bgcolor: '#fff' }}>
							<TraceabilityTimeline trace={trace} />
						</Paper>
					)}
				</div>
			</div>
		</>
	);
};

export default PublicTraceability;
